import { connect } from 'react-redux'
import config from 'config'
import deepEqual from 'fast-deep-equal'
import GTM from 'react-gtm-module'

import {
  selectIsOnLoginPage,
  selectIsOnForgotPasswordPage,
  selectIsOnForgotSubdomainPage,
  selectIsOnChangePasswordPage,
  selectIsOnOauthErrorPage,
} from 'selectors/app'

import {
  selectIsOnKbCreatePage,
  selectIsOnKbDashboardPage,
  selectIsOnKbThemePage,
  selectIsOnKbArticlesPage,
  selectIsOnKbArticleCreatePage,
  selectIsOnKbArticleEditPage,
  selectIsOnKbCategoriesPage,
  selectIsOnKbCategoryCreatePage,
  selectIsOnKbCategoryEditPage,
  selectIsOnKbSettingsPage,
  selectIsOnKbUpgradePage,
  selectIsOnKbUpgradeProPage,
} from 'subapps/kb/selectors/pages'
import { selectCurrentPath } from 'selectors/location'

// Note: Changed to selectors directly to prevent circular references
import { selectIsOnConversationsPage } from 'ducks/reports/conversations/selectors'
import { selectIsOnProductivityPage } from 'ducks/reports/productivity/selectors'
import { selectIsOnHappinessPage } from 'ducks/reports/happiness/selectors'
import { selectIsOnArticlesPage } from 'ducks/reports/kb/selectors'
import { selectIsOnAgentsOverviewPage } from 'ducks/reports/agents/selectors'
import { selectIsOnTeamsOverviewPage } from 'ducks/reports/teams/selectors'
import {
  selectIsOnAgentOverviewPage,
  selectIsOnTeamOverviewPage,
} from 'ducks/reports/detail/selectors'
import {
  selectIsOnSignupPage as selectIsOnOnboardingSignupPage,
  selectIsOnCustomerInformationPage as selectIsOnOnboardingCustomerInformationPage,
  selectIsOnChannelOverviewPage as selectIsOnOnboardingChannelOverviewPage,
  selectIsOnAddTeamMembersPage as selectIsOnOnboardingAddTeamMembersPage,
  selectIsOnAddChannelPage as selectIsOnOnboardingAddChannelPage,
  selectIsOnSetupEmailPage as selectIsOnOnboardingSetupEmailPage,
  selectIsOnCompletedPage as selectIsOnOnboardingCompletedPage,
  selectIsOnV2SignupPage as selectIsOnOnboardingV2SignupPage,
  selectIsOnV2CompanyPage as selectIsOnOnboardingV2CompanyPage,
  selectIsOnV2OwnerPage as selectIsOnOnboardingV2OwnerPage,
  selectIsOnV2PasswordPage as selectIsOnOnboardingV2PasswordPage,
  selectIsOnV2VerificationPage as selectIsOnOnboardingV2VerificationPage,
  selectIsOnV2CompletionPage as selectIsOnOnboardingV2CompletionPage,
} from 'subapps/onboarding/selectors'
import { selectCurrentUser } from 'ducks/currentUser/selectors'

const select = state => {
  return {
    onLoginPage: selectIsOnLoginPage(state),
    onForgotPasswordPage: selectIsOnForgotPasswordPage(state),
    onForgotSubdomainPage: selectIsOnForgotSubdomainPage(state),
    onChangePasswordPage: selectIsOnChangePasswordPage(state),
    onOauthErrorPage: selectIsOnOauthErrorPage(state),
    onKbCreatePage: selectIsOnKbCreatePage(state),
    onKbDashboardPage: selectIsOnKbDashboardPage(state),
    onKbThemePage: selectIsOnKbThemePage(state),
    onKbArticlesPage: selectIsOnKbArticlesPage(state),
    onKbArticleCreatePage: selectIsOnKbArticleCreatePage(state),
    onKbArticleEditPage: selectIsOnKbArticleEditPage(state),
    onKbCategoriesPage: selectIsOnKbCategoriesPage(state),
    onKbCategoryCreatePage: selectIsOnKbCategoryCreatePage(state),
    onKbCategoryEditPage: selectIsOnKbCategoryEditPage(state),
    onKbSettingsPage: selectIsOnKbSettingsPage(state),
    onKbUpgradePage: selectIsOnKbUpgradePage(state),
    onKbUpgradeProPage: selectIsOnKbUpgradeProPage(state),
    path: selectCurrentPath(state),
    onReportsInboxConversationsPage: selectIsOnConversationsPage(state),
    onReportsInboxProductivityPage: selectIsOnProductivityPage(state),
    onReportsInboxAgentOverviewPage: selectIsOnAgentOverviewPage(state),
    onReportsInboxTeamOverviewPage: selectIsOnTeamOverviewPage(state),
    onReportsInboxAgentsOverviewPage: selectIsOnAgentsOverviewPage(state),
    onReportsInboxTeamsOverviewPage: selectIsOnTeamsOverviewPage(state),
    onReportsInboxHappinessPage: selectIsOnHappinessPage(state),
    onReportsKbArticlesPage: selectIsOnArticlesPage(state),
    onOnboardingSignupPage: selectIsOnOnboardingSignupPage(state),
    onOnboardingCustomerInformationPage: selectIsOnOnboardingCustomerInformationPage(
      state
    ),
    onOnboardingChannelOverviewPage: selectIsOnOnboardingChannelOverviewPage(
      state
    ),
    onOnboardingAddTeamMembersPage: selectIsOnOnboardingAddTeamMembersPage(
      state
    ),
    onOnboardingAddChannelPage: selectIsOnOnboardingAddChannelPage(state),
    onOnboardingSetupEmailPage: selectIsOnOnboardingSetupEmailPage(state),
    onOnboardingCompletedPage: selectIsOnOnboardingCompletedPage(state),
    onOnboardingV2SignupPage: selectIsOnOnboardingV2SignupPage(state),
    onOnboardingV2CompanyPage: selectIsOnOnboardingV2CompanyPage(state),
    onOnboardingV2OwnerPage: selectIsOnOnboardingV2OwnerPage(state),
    onOnboardingV2PasswordPage: selectIsOnOnboardingV2PasswordPage(state),
    onOnboardingV2VerificationPage: selectIsOnOnboardingV2VerificationPage(
      state
    ),
    onOnboardingV2CompletionPage: selectIsOnOnboardingV2CompletionPage(state),
    userId: selectCurrentUser(state).id,
  }
}

let lastPageDataLayer = null
function trackPage(
  pageType,
  pageGroup = '',
  userId = '',
  segment = config.gtm_segment,
  version = config.gtm_version,
  event = 'groove_nav_event'
) {
  if (!pageType) {
    throw new Error(
      'pageType, pageGroup, segment and version is required to track a page load'
    )
  }
  const dataLayer = {
    dataLayer: {
      segment,
      version,
      pageType,
      pageGroup,
      event,
      userId,
    },
    dataLayerName: 'PageDataLayer',
  }

  // All tracking in this module is "page" level tracking. You cant
  // fire the same page tracking twice because pages cant "navigate" to
  // themselves
  if (!deepEqual(lastPageDataLayer, dataLayer)) {
    lastPageDataLayer = dataLayer
    GTM.dataLayer(dataLayer)
  }
  return null
}

const Tracking = props => {
  const {
    onLoginPage,
    onForgotPasswordPage,
    onForgotSubdomainPage,
    onChangePasswordPage,
    onOauthErrorPage,
    onKbCreatePage,
    onKbDashboardPage,
    onKbThemePage,
    onKbArticlesPage,
    onKbArticleCreatePage,
    onKbArticleEditPage,
    onKbCategoriesPage,
    onKbCategoryCreatePage,
    onKbCategoryEditPage,
    onKbSettingsPage,
    onKbUpgradePage,
    onKbUpgradeProPage,
    path,
    onReportsInboxConversationsPage,
    onReportsInboxProductivityPage,
    onReportsInboxAgentOverviewPage,
    onReportsInboxTeamOverviewPage,
    onReportsInboxAgentsOverviewPage,
    onReportsInboxTeamsOverviewPage,
    onReportsInboxHappinessPage,
    onReportsKbArticlesPage,
    onOnboardingSignupPage,
    onOnboardingCustomerInformationPage,
    onOnboardingChannelOverviewPage,
    onOnboardingAddTeamMembersPage,
    onOnboardingAddChannelPage,
    onOnboardingSetupEmailPage,
    onOnboardingCompletedPage,
    onOnboardingV2SignupPage,
    onOnboardingV2CompanyPage,
    onOnboardingV2OwnerPage,
    onOnboardingV2PasswordPage,
    onOnboardingV2VerificationPage,
    onOnboardingV2CompletionPage,
    userId,
  } = props

  if (onLoginPage) return trackPage('login', 'login', userId)
  if (onForgotPasswordPage) return trackPage('forgot-password', 'login', userId)
  if (onForgotSubdomainPage)
    return trackPage('forgot-subdomain', 'login', userId)
  if (onChangePasswordPage) return trackPage('change-password', 'login', userId)
  if (onOauthErrorPage) return trackPage('oauth-error', 'login', userId)

  if (path.match('storybook'))
    return trackPage('storybook', 'development', userId)

  // Settings
  if (path.match('settings/business_hours'))
    return trackPage('business-hours', 'settings', userId)
  if (path.match('settings/billing'))
    return trackPage('billing', 'settings', userId)
  if (path.match('settings/integrations'))
    return trackPage('integrations-beta', 'settings', userId)

  // Inbox
  if (path === '/') return trackPage('home', 'inbox', userId)
  if (path.match(/^\/search/)) return trackPage('search', 'inbox', userId)
  if (path.match(/^\/tickets/)) return trackPage('ticket', 'inbox', userId)

  // Onboarding
  if (onOnboardingSignupPage) return trackPage('signup', 'onboarding', userId)
  if (onOnboardingCustomerInformationPage)
    return trackPage('your-business', 'onboarding', userId)
  if (onOnboardingChannelOverviewPage)
    return trackPage('channel-overview', 'onboarding', userId)
  if (onOnboardingAddTeamMembersPage)
    return trackPage('add-team', 'onboarding', userId)
  if (onOnboardingAddChannelPage)
    return trackPage('add-channel', 'onboarding', userId)
  if (onOnboardingSetupEmailPage)
    return trackPage('add-channel-email-forwarding', 'onboarding', userId)
  if (onOnboardingCompletedPage)
    return trackPage('completed', 'onboarding', userId)

  if (onOnboardingV2SignupPage)
    return trackPage('v2-signup', 'onboarding', userId)
  if (onOnboardingV2CompanyPage)
    return trackPage('v2-company', 'onboarding', userId)
  if (onOnboardingV2OwnerPage)
    return trackPage('v2-owner', 'onboarding', userId)
  if (onOnboardingV2PasswordPage)
    return trackPage('v2-password', 'onboarding', userId)
  if (onOnboardingV2VerificationPage)
    return trackPage('v2-verification', 'onboarding', userId)
  if (onOnboardingV2CompletionPage)
    return trackPage('v2-completion', 'onboarding', userId)

  // Reports
  if (onReportsInboxConversationsPage)
    return trackPage('inbox-conversations', 'reports', userId)
  if (onReportsInboxProductivityPage)
    return trackPage('inbox-productivity', 'reports', userId)
  if (onReportsInboxAgentOverviewPage)
    return trackPage(`inbox-agent`, 'reports', userId)
  if (onReportsInboxTeamOverviewPage)
    return trackPage(`inbox-team`, 'reports', userId)
  if (onReportsInboxAgentsOverviewPage)
    return trackPage('inbox-agents', 'reports', userId)
  if (onReportsInboxTeamsOverviewPage)
    return trackPage('inbox-teams', 'reports', userId)
  if (onReportsInboxHappinessPage)
    return trackPage('inbox-happiness', 'reports', userId)
  if (onReportsKbArticlesPage)
    return trackPage('kb-articles', 'reports', userId)

  // KB
  if (onKbCreatePage) return trackPage('create', 'kb', userId)
  if (onKbDashboardPage) return trackPage('dashboard', 'kb', userId)
  if (onKbThemePage) return trackPage('editor-theme', 'kb', userId)
  if (onKbArticlesPage) return trackPage('listings-article', 'kb', userId)
  if (onKbArticleCreatePage) return trackPage('editor-article', 'kb', userId)
  if (onKbArticleEditPage) return trackPage('editor-article', 'kb', userId)
  if (onKbCategoriesPage) return trackPage('listing-category', 'kb', userId)
  if (onKbCategoryCreatePage) return trackPage('editor-category', 'kb', userId)
  if (onKbCategoryEditPage) return trackPage('editor-category', 'kb', userId)
  if (onKbSettingsPage) return trackPage('settings', 'kb', userId)
  if (onKbUpgradePage) return trackPage('trial-end', 'kb', userId)
  if (onKbUpgradeProPage) return trackPage('upgrade', 'kb', userId)

  return null
}

export default connect(select)(Tracking)
